import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import useSWR from 'swr';
import { OutpostTenant } from '../../../../../../../typings/outpost';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { GlobalContext } from '../../../contexts/GlobalContext';
const OUTPOST_TENANT_NAME_METADATA_KEYS = new Set(['display_name', 'displayname', 'name']);
const normalizeTenantMetadataKey = (key: string): string => key.trim().toLowerCase().replace(/[\s-]+/g, '_');
const formatTenantMetadataValue = (value: unknown): string | undefined => {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmed_value = value.trim();
    return trimmed_value || undefined;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return undefined;
};
export const getOutpostTenantDisplayName = (tenant?: Pick<OutpostTenant, 'metadata'> | {
  metadata?: Record<string, unknown>;
} | null): string | undefined => {
  if (!tenant?.metadata) {
    return undefined;
  }
  for (const [key, value] of Object.entries(tenant.metadata)) {
    if (!OUTPOST_TENANT_NAME_METADATA_KEYS.has(normalizeTenantMetadataKey(key))) {
      continue;
    }
    const formatted_value = formatTenantMetadataValue(value);
    if (formatted_value) {
      return formatted_value;
    }
  }
  return undefined;
};
export const getOutpostTenantLabel = (tenant_id: string, tenant?: Pick<OutpostTenant, 'metadata'> | {
  metadata?: Record<string, unknown>;
} | null): string => {
  const display_name = getOutpostTenantDisplayName(tenant);
  return display_name ? `${display_name} (${tenant_id})` : tenant_id;
};
export const filterOutpostTenantBySearchTerm = (tenant: Pick<OutpostTenant, 'id' | 'metadata'>, search_term: string): boolean => {
  const normalized_search_term = search_term.trim().toLowerCase();
  if (!normalized_search_term) {
    return true;
  }
  if (tenant.id.toLowerCase().includes(normalized_search_term)) {
    return true;
  }
  const display_name = getOutpostTenantDisplayName(tenant);
  return display_name ? display_name.toLowerCase().includes(normalized_search_term) : false;
};
export const useOutpostTenantsById = tenant_ids => {
  const $ = _c(11);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let filters;
  let t0;
  let t1;
  if ($[0] !== tenant_ids) {
    const unique_tenant_ids = Array.from(new Set(tenant_ids.filter(_temp))).sort();
    filters = unique_tenant_ids.length > 0 ? {
      id: unique_tenant_ids,
      limit: unique_tenant_ids.length
    } : null;
    t0 = useSWR;
    t1 = filters ? APIMethodKeys.outpost.tenants.list(filters) : null;
    $[0] = tenant_ids;
    $[1] = filters;
    $[2] = t0;
    $[3] = t1;
  } else {
    filters = $[1];
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== HookdeckAPI.outpost.tenants || $[5] !== filters) {
    t2 = () => HookdeckAPI.outpost.tenants.list(filters || {
      limit: 1
    });
    $[4] = HookdeckAPI.outpost.tenants;
    $[5] = filters;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    data
  } = t0(t1, t2);
  let t3;
  if ($[7] !== data?.models) {
    t3 = (data?.models || []).reduce(_temp2, {} as Record<string, OutpostTenant>);
    $[7] = data?.models;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const tenants_by_id = t3;
  let t4;
  if ($[9] !== tenants_by_id) {
    t4 = {
      tenants_by_id
    };
    $[9] = tenants_by_id;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
function _temp(tenant_id) {
  return !!tenant_id;
}
function _temp2(result, tenant) {
  result[tenant.id] = tenant;
  return result;
}